import styled from "styled-components";
import { BORDERS, COLOURS } from "../../variables/Colors";
import { BREAKPOINTS, desktop, mobile } from "../../variables/Breakpoints";
import { setFlex, setPosition } from "../../variables/Position";

const randomiseJustify = id => {
  const modes = ["center", "flex-start", "flex-end"];
  return modes[id % modes.length];
};

export const Headline = styled.h3`
  font-family: gantserif-medium-condensed;
  font-weight: 500;
  color: ${COLOURS.primary.black};
  margin: 0;
  text-align: left;
  font-size: 1.1rem;
  line-height: 1.2rem;
  &.headline,
  .headline {
    margin: 1.5rem 0;
    font-size: 2rem;
    line-height: 2rem;
    @media (max-width: ${BREAKPOINTS.sm}px) {
      font-size: 1.8rem;
      line-height: 1.8rem;
    }
  }
  @media (max-width: ${BREAKPOINTS.sm}px) {
    font-size: 1.2rem;
    line-height: 1.4rem;
  }
  ${props => props.$margin && `margin-top: 1rem;`};
`;

export const Link = styled.a`
  font-family: gantserif-medium-condensed;
  font-weight: 500;
  color: ${COLOURS.primary.black};
  text-decoration: underline;
`;

export const Subheading = styled.h4`
  font-family: gantmodern-regular;
  font-weight: 500;
  color: ${COLOURS.primary.black};
  margin: 0 0 0 -1px;
  text-align: left;
`;

export const MiniHeading = styled.h5`
  font-family: gantmodern-regular;
  font-weight: 500;
  color: ${COLOURS.primary.black};
  text-align: center;
  margin: 0;
  font-size: 0.9rem;
  line-height: 0.9rem;
  ${props =>
    props.$symbol &&
    `
    opacity: 0;
    margin-right: 0.5rem
  `};
  ${props => props.$active && "opacity: 1;"};
`;

export const PDPId = styled(Subheading)`
  font-size: 0.9rem;
  ${props => props.$whiteWrap && "white-space: nowrap;"};
`;

export const ArchiveHeading = styled(Subheading)`
  font-size: 0.8rem;
`;

export const Wrapper = styled.div`
  ${setPosition({ position: "sticky", left: "0", bottom: 50, zIndex: 5 })};
  ${setFlex({ justify: "center", align: "flex-end" })};
  height: fit-content;
  width: 100%;
  &.fixed {
    ${setPosition({ position: "fixed" })};
  }
  @media (max-width: ${BREAKPOINTS.sm}px) {
    ${setPosition({ position: "fixed", top: 150, zIndex: 20 })};
    margin: 0 1rem;
    width: -webkit-fill-available;
    height: -moz-available;
  }
  ${props => props.$desktop && desktop};
  ${props => props.$mobile && mobile};
  will-change: transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
`;

export const MobileNavigation = styled.div`
  ${setPosition({ position: "sticky", top: "0", zIndex: 20 })};
  ${setFlex({ justify: "space-between" })};
  width: -webkit-fill-available;
  width: -moz-available;
  padding: 0 1rem;
  ${mobile};
`;

export const Button = styled.button`
  ${setFlex({ justify: "center", align: "center" })};
  border: 1px solid ${BORDERS.secondary};
  border-radius: 5px;
  background-color: ${COLOURS.primary.white};
  padding: 0.5rem 1rem;
  width: 50%;
  margin: 0 0.5rem;
  cursor: pointer;
  ${props => props.$noMargin && `margin-${props.$noMargin}: 0;`}
  ${props =>
    props.$opened &&
    `
    background: transparent;
    border: none;
  `}
  &:not(.active) {
    opacity: 0;
  }
`;

export const CloseImage = styled.img`
  ${props =>
    setPosition({
      position: "absolute",
      right: props.$right ? 5 : null,
      left: props.$left ? 5 : null,
    })};
  width: auto;
  margin: 0 1rem;
`;

export const OverflowContainer = styled.div`
  ${setFlex({ direction: "column" })};
  border: 1px solid ${COLOURS.primary.black};
  max-width: 900px;
  width: 80vw;
  overflow: hidden;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    max-width: 100vw;
    width: -webkit-fill-available;
    width: -moz-available;
    overflow: visible;
    border: none;
  }
`;

export const Container = styled.div`
  ${setFlex({ align: "center" })};
  margin-bottom: 2rem;
  border: 1px solid ${BORDERS.secondary};
  border-radius: 5px;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    ${setFlex({ align: "flex-start", direction: "column" })};
    height: fit-content;
    width: 100%;
    min-width: 80vw;
    max-height: 60vh;
    overflow: scroll;
    overflow-x: hidden;
    margin: 0;
    border: none;
    overscroll-behavior: none;
    ${props => props.$active && `border: 1px solid ${BORDERS.secondary};`}
  }
  will-change: transform;
  backface-visibility: hidden;
  transform-style: preserve-3d;
  -webkit-transform-style: preserve-3d;
  > div {
    -webkit-transform: translate3d(0, 0, 0);
    transform: translate3d(0, 0, 0);
  }
  @media (min-width: ${BREAKPOINTS.sm + 1}px) {
    transform: scale(0.5, 0.5);
  }
`;

export const ContentContainer = styled.div`
  ${setFlex({ justify: "center", align: "center" })};
  background-color: ${COLOURS.primary.white};
  cursor: pointer;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    background-color: unset;
  }
`;

export const NavigationWrapper = styled.div`
  ${setPosition({ position: "relative" })};
  ${setFlex({ justify: "flex-start", align: "center" })};
  width: fit-content;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    ${setFlex({ direction: "column" })};
    width: 100%;
    &:not(.active) {
      display: none;
    }
  }
  transform-style: preserve-3d;
  -webkit-transform-style: preserve-3d;
  will-change: transform;
`;

export const ArchiveWrapper = styled.div`
  ${setPosition({ position: "relative" })};
  ${setFlex({ justify: "flex-start", align: "center" })};
  width: 100%;
  &:not(.active) {
    display: none;
  }
  ${mobile}
`;

export const ArchiveContent = styled.div`
  padding: 4rem 1.5rem;
  width: -webkit-fill-available;
  width: -moz-available;
`;

export const NavigationContainer = styled.div`
  ${setFlex({})};
  @media (max-width: ${BREAKPOINTS.sm}px) {
    ${props =>
      setFlex({
        justify: randomiseJustify(props.$justifyId),
      })};
    width: 100%;
    padding: 1rem 0;
  }
`;

export const ImageContainer = styled.div`
  ${setFlex({})};
  width: ${props =>
    `calc(${props.$aspectRatio * 20}vh - ${props.$aspectRatio * 10}px)`};
  height: auto;
  pointer-events: none;
  @media (max-width: ${BREAKPOINTS.sm}px) {
    ${props =>
      props.$aspectRatio &&
      `height: calc(${20 * props.$aspectRatio}vh - ${
        20 * props.$aspectRatio
      }px)`};
    width: auto;
  }
`;

export const Image = styled.img`
  width: inherit;
  height: inherit;
  max-width: unset;
  pointer-events: none;
`;

export const PLPContent = styled.div`
  ${setPosition({ position: "static" })};
  ${setFlex({ align: "center" })};
  height: 100%;
  padding-right: 1rem;
`;

export const ArchiveContainer = styled.div`
  ${setPosition({ position: "absolute", right: -80, zIndex: 10 })};
  background-color: ${COLOURS.primary.white};
  border: 1px solid ${COLOURS.primary.black};
  padding: 0.8rem 0.5rem;
  cursor: pointer;
  @media (min-width: ${BREAKPOINTS.sm}px) {
    &:hover {
      background-color: ${COLOURS.primary.black};
      h4 {
        color: ${COLOURS.primary.white};
      }
    }
  }
  ${desktop}
`;

export const FrostBackdrop = styled.div`
  ${setPosition({ position: "relative", zIndex: 1 })};
  width: 100%;
  height: auto;
  background: inherit;
  will-change: transform;

  @media (max-width: ${BREAKPOINTS.sm}px) {
    ${props =>
      props.$active &&
      `
      &:before {
        ${setPosition({
          position: "absolute",
          top: "0",
          left: "0",
          right: "0",
          bottom: "0",
          zIndex: -1,
        })};
        content: "";
        height: 100%;
        will-change: transform;
        background: inherit;
        backdrop-filter: blur(10px);
        backface-visibility: hidden;
        transform: translate3d(0, 0, 0);
        -webkit-backface-visibility: hidden;
        -webkit-transform: translate3d(0, 0, 0);
        -webkit-backdrop-filter: blur(10px);
        background: linear-gradient(
          88deg,
          rgba(255, 255, 255, 0.1) 51.69%,
          rgba(255, 255, 255, 0) 150.79%
        );
      }
    `}
  }
`;
